/**
 * Zod schemas validated at the API boundary (spec §4, §6), plus predicates
 * derived from them (e.g. visibility rules). Every admin mutation parses its
 * body through one of the schemas here before the data layer runs.
 */
import { z } from "zod";
import { aboutSchema, emptyAbout } from "./about.js";
import { brandColorSchema } from "./brandRamp.js";
import { httpUrlSchema } from "./validation.js";
import { ageGroupSchema, classificationSchema, seasonSchema } from "./teamAttributes.js";
import { usStateSchema } from "./usStates.js";
// ---------------------------------------------------------------------------
// Shared enums / fragments
// ---------------------------------------------------------------------------
export const tournamentStatusSchema = z.enum(["draft", "published", "complete", "archived"]);
/**
 * The statuses the public may see. An ALLOWLIST on purpose: the visibility
 * gates were once written as `status !== "draft"`, so adding a status made it
 * public by default with no compile error. Adding a status here must be a
 * deliberate act.
 */
export const PUBLIC_STATUSES = [
    "published",
    "complete",
];
export function isPubliclyVisible(status) {
    return PUBLIC_STATUSES.some((s) => s === status);
}
export const gameStatusSchema = z.enum(["scheduled", "final", "forfeit", "canceled"]);
export const gameTypeSchema = z.enum(["pool", "bracket"]);
export const tournamentConfigSchema = z.object({
    runCapEnabled: z.boolean().default(true),
    runCapMargin: z.number().int().min(0).default(8),
    forfeitScore: z
        .object({ winner: z.number().int().min(0), loser: z.number().int().min(0) })
        .default({ winner: 7, loser: 0 }),
    gameLengthMinutes: z.number().int().positive().default(90),
    minRestMinutes: z.number().int().min(0).default(30),
});
const defaultConfig = () => tournamentConfigSchema.parse({});
export const registrationWindowSchema = z
    .object({ opensAt: z.string().min(1), closesAt: z.string().min(1) })
    .nullable();
/**
 * How a division's championship bracket is built (spec §5, §7.10). The
 * director sets it before Create bracket, and `engine.generateBracket` reads
 * it: `"all"` brackets every team in the division, a number takes the top N by
 * division seed and gives the teams above the next power of two a bye.
 *
 * The minimum is 2, not 1. A one-team bracket generates no games at all —
 * single elimination needs somebody to eliminate — so saving `1` would leave a
 * division whose Create bracket button silently produces nothing.
 */
export const bracketConfigSchema = z.object({
    format: z.literal("singleElim").default("singleElim"),
    advance: z.union([z.literal("all"), z.number().int().min(2)]).default("all"),
});
const defaultBracket = () => bracketConfigSchema.parse({});
/**
 * Create bracket's body. `replace` is the rebuild an admin reaches for after
 * changing `advance` — it tears the existing bracket down and generates a new
 * one in a single call, carrying each game's time and place across to the game
 * that takes its place in the new shape.
 *
 * Defaulted rather than required so the button that has always posted no body
 * keeps meaning what it meant: create one, and refuse if there already is one.
 */
export const createBracketSchema = z.object({
    replace: z.boolean().default(false),
});
// ---------------------------------------------------------------------------
// Tournament / division / pool
// ---------------------------------------------------------------------------
export const createTournamentSchema = z.object({
    name: z.string().trim().min(1).max(160),
    startDate: z.string().min(1),
    endDate: z.string().min(1),
    description: z.string().trim().max(2000).default(""),
    location: z.string().trim().max(200).default(""),
    /** Hero image on the public tournament page. Null clears it; omitted leaves it untouched. */
    bannerImageUrl: z.string().trim().url().max(2000).nullable().default(null),
    state: usStateSchema.optional(),
    timezone: z.string().min(1).default("America/Chicago"),
    status: tournamentStatusSchema.default("draft"),
    venueIds: z.array(z.string()).default([]),
    registration: registrationWindowSchema.default(null),
    config: tournamentConfigSchema.default(defaultConfig),
});
export const updateTournamentSchema = createTournamentSchema.partial();
/**
 * What an organizing coach sends to create their own event (self-serve spec §6).
 * Deliberately not `createTournamentSchema`: that one lets a global admin choose
 * a status and skip the state, and this route must not be able to do either.
 * `status` is absent, so a body that carries one fails rather than being stripped
 * into a published tournament.
 */
export const selfServeTournamentSchema = z
    .object({
    name: z.string().trim().min(1).max(160),
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    location: z.string().trim().min(1).max(200),
    state: usStateSchema,
    ageGroups: z.array(ageGroupSchema).min(1),
    timezone: z.string().min(1).default("America/Chicago"),
    config: tournamentConfigSchema.optional(),
})
    .strict()
    .refine((v) => v.state !== "", { path: ["state"], message: "Pick a state" })
    .refine((v) => v.endDate >= v.startDate, {
    path: ["endDate"],
    message: "The end date is before the start date",
});
/**
 * Image uploads — the tournament banner (issue #22) and the site logo. The
 * admin asks the API for a presigned URL, uploads the bytes straight to
 * storage, then saves the returned URL into `bannerImageUrl` above or
 * `logoUrl` below. So these describe the *request for a URL*, not the file,
 * and no record is changed by any of it.
 *
 * SVG is absent on purpose: it is a script host, and served from the media
 * domain a `<script>` inside one would execute with that origin. The logo is
 * where that gets tempting — vector is the natural format for a mark — but the
 * mark renders at 28px, where a 2x PNG is indistinguishable.
 */
export const imageContentTypeSchema = z.enum([
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/gif",
]);
/** Stored object extension per accepted type. A Record so adding a type breaks the build. */
export const IMAGE_EXTENSIONS = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
};
/**
 * The size the browser refuses to upload past. The server checks the declared
 * size too, which is an early, honest error — NOT enforcement: a presigned PUT
 * cannot bound the body it signs (only a POST policy carries
 * `content-length-range`). The route is admin-only, so what remains is a
 * trusted director uploading something large.
 */
export const MAX_BANNER_BYTES = 5 * 1024 * 1024;
/**
 * Smaller than the banner's, deliberately. The logo is in the nav and the
 * footer, so *every* visitor fetches it on *every* page; the banner is one
 * image on one page. It also renders into a 28px box. A megabyte is already
 * generous for a square mark, and an admin on an office connection would never
 * feel the cost of a four-megabyte one.
 */
export const MAX_LOGO_BYTES = 1024 * 1024;
const uploadRequest = (maxBytes) => z.object({
    contentType: imageContentTypeSchema,
    size: z.number().int().positive().max(maxBytes),
});
export const bannerUploadSchema = uploadRequest(MAX_BANNER_BYTES);
export const logoUploadSchema = uploadRequest(MAX_LOGO_BYTES);
export const createDivisionSchema = z.object({
    name: z.string().trim().min(1).max(40),
    order: z.number().int().min(0).default(0),
    maxTeams: z.number().int().positive().optional(),
    bracket: bracketConfigSchema.default(defaultBracket),
});
export const updateDivisionSchema = createDivisionSchema.partial().extend({
    /** `null` clears the cap; omitted leaves it as it is (console spec §6.3). */
    maxTeams: z.number().int().positive().nullable().optional(),
});
export const createPoolSchema = z.object({
    divisionId: z.string().min(1),
    name: z.string().trim().min(1).max(40),
    order: z.number().int().min(0).default(0),
});
/**
 * Rename or reorder (console spec §6.1). A pool never moves between
 * divisions — its entries and games would keep the old `divisionId` — so a
 * body naming one is refused outright rather than ignored.
 */
export const updatePoolSchema = createPoolSchema.omit({ divisionId: true }).partial().strict();
// ---------------------------------------------------------------------------
// Venue
// ---------------------------------------------------------------------------
export const venueFieldSchema = z.object({
    name: z.string().trim().min(1).max(60),
    notes: z.string().trim().max(200).optional(),
});
// ---------------------------------------------------------------------------
// Admin allowlist
// ---------------------------------------------------------------------------
/**
 * Granting admin takes an email because that is what an organizer knows; the
 * allowlist is keyed by Cognito sub, so the server resolves one to the other.
 * Lowercased here so the Cognito filter and the stored copy always agree.
 */
export const addAdminSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(320),
});
// ---------------------------------------------------------------------------
// Roles (issue: admin users + roles)
// ---------------------------------------------------------------------------
/**
 * Roles granted on one specific tournament. A separate axis from the global
 * allowlist above: holding one says nothing about the account's global tier,
 * and a global admin outranks every one of these without ever holding one.
 */
export const tournamentRoleSchema = z.enum(["tournamentAdmin", "tournamentScorer"]);
/**
 * The global tier an admin can move an account between. "coach" is not a stored
 * grant — it is the absence of an allowlist row — so setting it means revoking,
 * which is why this is an enum of two states rather than a boolean flag.
 */
export const globalRoleSchema = z.enum(["admin", "coach"]);
export const setGlobalRoleSchema = z.object({ role: globalRoleSchema });
export const grantTournamentRoleSchema = z.object({ role: tournamentRoleSchema });
/**
 * How a tournament director grants a scorer: they know an email, not a Cognito
 * sub. Same address rules as adding an organization admin, plus the role so
 * the handler can refuse `tournamentAdmin` from anyone who is not one.
 */
export const grantTournamentRoleByEmailSchema = addAdminSchema.extend({
    role: tournamentRoleSchema,
});
// ---------------------------------------------------------------------------
// Site branding (issue #20): a single app-wide record, not per-tournament.
// ---------------------------------------------------------------------------
/** Shown across the app until an admin sets their own — the app's own brand. */
export const DEFAULT_SITE_SETTINGS = {
    orgName: "Alpha Tournaments",
    tagline: "Youth baseball, run beautifully.",
    logoUrl: null,
    brandColor: null,
    /**
     * Evaluated once, so every unconfigured deployment shares this instance —
     * fine because nothing mutates a settings record: the API returns it as JSON
     * and the admin form copies it into local state before editing. Anything that
     * wants a mutable empty About should call `emptyAbout()` for its own.
     */
    about: emptyAbout(),
};
export const siteSettingsSchema = z.object({
    orgName: z.string().trim().min(1).max(80).default(DEFAULT_SITE_SETTINGS.orgName),
    tagline: z.string().trim().max(160).default(DEFAULT_SITE_SETTINGS.tagline),
    /** Replaces the nav/footer mark when set. Null clears it; omitted leaves it untouched. */
    logoUrl: z.string().trim().url().max(2000).nullable().default(DEFAULT_SITE_SETTINGS.logoUrl),
    /**
     * The accent color, as `#rrggbb`. The app derives all eleven shades from it
     * (see `deriveBrandRamp`). Null restores the app's own blue.
     */
    brandColor: brandColorSchema.default(DEFAULT_SITE_SETTINGS.brandColor),
    /**
     * The About page. Replaced wholesale, never patched: `updateSiteSettings`
     * merges shallowly, so a PUT carrying `about.sections` and no `about.contact`
     * drops the contact block. The admin page always sends both halves.
     */
    about: aboutSchema.default(() => aboutSchema.parse({})),
});
export const updateSiteSettingsSchema = siteSettingsSchema.partial();
export const createVenueSchema = z.object({
    name: z.string().trim().min(1).max(120),
    address: z.string().trim().min(1).max(240),
    mapsUrl: httpUrlSchema.optional(),
    fields: z.array(venueFieldSchema).default([]),
});
export const updateVenueSchema = createVenueSchema.partial();
// ---------------------------------------------------------------------------
// Team / entry
// ---------------------------------------------------------------------------
/**
 * What every team has regardless of who created it. A team is identified by
 * its age group and classification as much as by its name: "River Bandits" is
 * a different team at 9U AAA than at 12U Major.
 */
const teamCoreSchema = z.object({
    name: z.string().trim().min(1).max(120),
    city: z.string().trim().max(120).optional(),
    state: z.string().trim().max(60).optional(),
    ageGroup: ageGroupSchema,
    classification: classificationSchema,
});
/**
 * The coach path (`POST /me/teams`). Season is required here and only here: a
 * coach's team belongs to a season, and My Teams orders by it.
 */
export const createTeamSchema = teamCoreSchema.extend({ season: seasonSchema });
/**
 * The two admin paths (`POST /admin/teams` and quick-add's `newTeam`). No
 * season: an admin adding a walk-up team to a bracket is not filing it under a
 * season, and those teams carry no `ownerUid` so they never reach My Teams,
 * which is the only surface that orders by season.
 */
export const adminCreateTeamSchema = teamCoreSchema;
export const updateTeamSchema = createTeamSchema.partial();
/** Admin search-or-create quick-add: exactly one of teamId | newTeam (spec §8.4). */
export const quickAddEntrySchema = z
    .object({
    divisionId: z.string().min(1),
    poolId: z.string().nullable().default(null),
    teamId: z.string().min(1).optional(),
    newTeam: adminCreateTeamSchema.optional(),
})
    .refine((v) => Boolean(v.teamId) !== Boolean(v.newTeam), {
    message: "provide exactly one of teamId or newTeam",
});
export const assignPoolSchema = z.object({ poolId: z.string().nullable() });
/** Finalize seeding: the full seed order (index 0 = seed 1), spec §7.9. */
export const finalizeSeedingSchema = z.object({
    order: z.array(z.string().min(1)).min(1),
});
// ---------------------------------------------------------------------------
// Game
// ---------------------------------------------------------------------------
/**
 * A game's when + where. Every key is nullable on the wire: an explicit `null`
 * clears the stored value, while an omitted key leaves it untouched (spec §8.6).
 * `scheduledAt` is a UTC instant — exactly what `Date#toISOString()` emits.
 */
export const gameScheduleSchema = z.object({
    scheduledAt: z.string().datetime().nullish(),
    venueId: z.string().nullish(),
    field: z.string().trim().max(60).nullish(),
});
export const createGameSchema = gameScheduleSchema.extend({
    divisionId: z.string().min(1),
    type: gameTypeSchema.default("pool"),
    poolId: z.string().optional(),
    homeTeamId: z.string().optional(),
    awayTeamId: z.string().optional(),
});
/**
 * A game's when + where, plus who is in it. The team slots are here rather than
 * on `gameScheduleSchema` because that object feeds `createGameSchema`, which
 * declares them already.
 *
 * They follow the same nullable convention as the keys above: an id assigns the
 * slot, `null` empties it back to TBD, and an omitted key leaves it untouched —
 * which is what lets an admin hand-build a pool schedule a slot at a time.
 * Bracket sides are NOT assignable this way; they derive from seeds and winners
 * (spec §7.10), and the repository rejects the attempt.
 */
export const updateGameSchema = gameScheduleSchema.partial().extend({
    homeTeamId: z.string().nullish(),
    awayTeamId: z.string().nullish(),
});
export const scoreSchema = z
    .object({
    status: gameStatusSchema,
    homeScore: z.number().int().min(0).optional(),
    awayScore: z.number().int().min(0).optional(),
    forfeitTeamId: z.string().optional(),
})
    .superRefine((val, ctx) => {
    if (val.status === "final" && (val.homeScore === undefined || val.awayScore === undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "final games require both scores" });
    }
    if (val.status === "forfeit" && !val.forfeitTeamId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "forfeit requires forfeitTeamId" });
    }
});
// ---------------------------------------------------------------------------
// Registration (user tier — validated here, created by user-api in Phase 4)
// ---------------------------------------------------------------------------
export const createRegistrationSchema = z.object({
    tournamentId: z.string().min(1),
    teamId: z.string().min(1),
    divisionId: z.string().min(1),
    /**
     * The pool the coach is registering for. Nullish rather than required
     * because pools are admin-created and a registration window routinely opens
     * before any exist — requiring one would make a poolless tournament
     * unregisterable. The form requires a choice whenever the chosen division
     * has pools to choose from, which is the same rule stated where it can
     * actually be checked.
     *
     * It seeds the entry's pool at approval; it does not lock it. An admin
     * still reassigns pools under Entries.
     */
    poolId: z.string().min(1).nullish(),
    contactName: z.string().trim().min(1).max(120),
    contactEmail: z.string().email(),
    contactPhone: z.string().trim().max(40).optional(),
});
export const rejectRegistrationSchema = z.object({
    internalNote: z.string().trim().max(1000).optional(),
});
// ---------------------------------------------------------------------------
// User profile (signup wizard step 2, spec §8.1)
// ---------------------------------------------------------------------------
export const updateProfileSchema = z.object({
    displayName: z.string().trim().min(1).max(120),
    phone: z.string().trim().max(40).optional(),
});
//# sourceMappingURL=schemas.js.map